// PublishLoader.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./PublishLoader.css";

const DEFAULT_MESSAGES = [
  "🚀 Preparing for launch...",
  "🎨 Designing your showcase...",
  "🌟 Almost there...",
  "🎉 Making it perfect...",
];

// Reusable loader
const PublishLoader = ({
  isOpen, // controlled visibility from parent
  onClose, // callback when loader is allowed to close
  minDuration = 2000, // minimum show time in ms
  messages = DEFAULT_MESSAGES,
  cycleInterval = 2000, // time between message changes
  children, // optional: custom animation content
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const startTimeRef = useRef(null);
  const closeRequestedRef = useRef(false);

  // Handle open/close with minimum duration
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      closeRequestedRef.current = false;
      startTimeRef.current = Date.now();
    } else if (isVisible) {
      // Parent wants to close; check minimum duration
      const elapsed = Date.now() - (startTimeRef.current || 0);
      if (elapsed >= minDuration) {
        setIsVisible(false);
        onClose?.();
      } else {
        closeRequestedRef.current = true;
        const remaining = minDuration - elapsed;
        const timeout = setTimeout(() => {
          if (closeRequestedRef.current) {
            setIsVisible(false);
            onClose?.();
          }
        }, remaining);
        return () => clearTimeout(timeout);
      }
    }
  }, [isOpen, isVisible, minDuration, onClose]);

  // Cycle messages
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, cycleInterval);
    return () => clearInterval(interval);
  }, [isVisible, messages.length, cycleInterval]);

  if (!isVisible) return null;

  // --- Variants (same as your original, moved inside reusable component) ---

  const rocketVariants = {
    animate: {
      y: [0, -15, 0, -15, 0],
      x: [0, 2, -2, 1, 0],
      rotate: [0, -2, 2, -1, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const flameVariants = {
    animate: (custom) => ({
      scaleY: [1, 1.4, 1, 1.3, 1],
      opacity: [0.8, 1, 0.7, 0.9, 0.8],
      transition: {
        duration: 0.3,
        repeat: Infinity,
        ease: "easeInOut",
        delay: custom * 0.1,
      },
    }),
  };

  const smokeVariants = {
    animate: (custom) => ({
      y: [0, 40],
      x: [0, custom % 2 === 0 ? -10 : 10],
      opacity: [0.6, 0],
      scale: [0.5, 1.5],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeOut",
        delay: custom * 0.3,
      },
    }),
  };

  const circleVariants = {
    animate: {
      scale: [1, 1.2, 1],
      rotate: [0, 360],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "linear",
      },
    },
  };

  const starVariants = {
    animate: (custom) => ({
      scale: [1, 1.5, 1],
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        delay: custom * 0.2,
        ease: "easeInOut",
      },
    }),
  };

  const dotVariants = {
    animate: (custom) => ({
      y: [0, -20, 0],
      transition: {
        duration: 0.6,
        repeat: Infinity,
        delay: custom * 0.15,
        ease: "easeInOut",
      },
    }),
  };

  const speedLineVariants = {
    animate: (custom) => ({
      y: [0, 50],
      opacity: [0, 0.6, 0],
      transition: {
        duration: 0.8,
        repeat: Infinity,
        ease: "linear",
        delay: custom * 0.15,
      },
    }),
  };

  return (
    <div className="publish-loader-overlay">
      <motion.div
        className="loader-glass-container"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.4 }}
      >
        <div className="loader-content">
          {/* Allow custom animation via children; fall back to rocket SVG */}
          {children ? (
            children
          ) : (
            <motion.svg
              width="200"
              height="200"
              viewBox="0 0 200 200"
              className="main-svg"
            >
              <motion.circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="url(#gradient1)"
                strokeWidth="3"
                strokeDasharray="5,5"
                variants={circleVariants}
                animate="animate"
              />

              {[0, 1, 2, 3].map((i) => (
                <motion.line
                  key={`speed-${i}`}
                  x1={85 + i * 10}
                  y1="120"
                  x2={85 + i * 10}
                  y2="130"
                  stroke="url(#gradient3)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity="0.4"
                  custom={i}
                  variants={speedLineVariants}
                  animate="animate"
                />
              ))}

              {[0, 1, 2].map((i) => (
                <motion.circle
                  key={`smoke-${i}`}
                  cx="100"
                  cy="120"
                  r="6"
                  fill="url(#smokeGradient)"
                  custom={i}
                  variants={smokeVariants}
                  animate="animate"
                />
              ))}

              <motion.g variants={rocketVariants} animate="animate">
                <rect
                  x="88"
                  y="70"
                  width="24"
                  height="35"
                  rx="2"
                  fill="url(#rocketGradient)"
                />

                <circle cx="100" cy="80" r="5" fill="#c4b5fd" opacity="0.9" />

                <polygon points="88,70 100,55 112,70" fill="#6366f1" />

                <polygon points="88,105 83,115 88,110" fill="#4f46e5" />
                <polygon points="112,105 117,115 112,110" fill="#4f46e5" />

                <rect
                  x="91"
                  y="88"
                  width="18"
                  height="2"
                  fill="#6366f1"
                  opacity="0.6"
                />
                <rect
                  x="91"
                  y="95"
                  width="18"
                  height="2"
                  fill="#6366f1"
                  opacity="0.6"
                />

                <g>
                  <motion.path
                    d="M 95 105 L 92 118 L 95 115 L 98 120 L 100 115 L 102 120 L 105 115 L 108 118 L 105 105 Z"
                    fill="url(#flameGradient1)"
                    custom={0}
                    variants={flameVariants}
                    animate="animate"
                  />

                  <motion.path
                    d="M 97 105 L 95 113 L 97 110 L 100 115 L 103 110 L 105 113 L 103 105 Z"
                    fill="url(#flameGradient2)"
                    custom={1}
                    variants={flameVariants}
                    animate="animate"
                  />

                  <motion.path
                    d="M 98 105 L 97 110 L 100 112 L 103 110 L 102 105 Z"
                    fill="url(#flameGradient3)"
                    custom={2}
                    variants={flameVariants}
                    animate="animate"
                  />
                </g>
              </motion.g>

              {[0, 1, 2, 3, 4].map((i) => (
                <motion.circle
                  key={i}
                  cx={100 + 70 * Math.cos((i * 2 * Math.PI) / 5)}
                  cy={100 + 70 * Math.sin((i * 2 * Math.PI) / 5)}
                  r="3"
                  fill="#a78bfa"
                  custom={i}
                  variants={starVariants}
                  animate="animate"
                />
              ))}

              <defs>
                <linearGradient
                  id="gradient1"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient
                  id="gradient2"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#c084fc" />
                </linearGradient>
                <linearGradient
                  id="gradient3"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0" />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.8" />
                </linearGradient>
                <linearGradient
                  id="rocketGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
                <linearGradient
                  id="flameGradient1"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#fbbf24" />
                  <stop offset="50%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#ef4444" />
                </linearGradient>
                <linearGradient
                  id="flameGradient2"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#fef3c7" />
                  <stop offset="50%" stopColor="#fbbf24" />
                  <stop offset="100%" stopColor="#f59e0b" />
                </linearGradient>
                <linearGradient
                  id="flameGradient3"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="50%" stopColor="#fef3c7" />
                  <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
                <radialGradient id="smokeGradient">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1" />
                </radialGradient>
              </defs>
            </motion.svg>
          )}

          {/* Bouncing dots */}
          <div className="bouncing-dots">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="dot"
                custom={i}
                variants={dotVariants}
                animate="animate"
              />
            ))}
          </div>

          {/* Animated message */}
          <motion.div
            className="loader-message"
            key={messageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            {messages[messageIndex]}
          </motion.div>

          {/* Progress bar */}
          <div className="progress-container">
            <motion.div
              className="progress-bar"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PublishLoader;
